import React, {useEffect, useMemo, useState} from 'react';
import {isEmpty} from 'lodash';
import {Route, Switch, useRouteMatch} from 'react-router-dom';
import UniversityForm from './form';
import AdminUniversityService, {
  AdminUniversity as AdminUniversityType,
} from 'src/services/AdminUniversityService';
import Loading from 'shared/components/loading';
import {useQuery} from 'src/hooks/useQuery';
import 'stylesheets/admin.scss';
import RecentDownloads from './form/RecentDownloads';
import req from 'shared/req';
import RoutedTabs, {Props as RoutedTabsProps} from '../../components/routed-tabs';
import EndpointConfigurationsTab from './endpoint-configurations-tab';
import ReportIntegrations from '../../components/report-integrations';
import {withFeatureFlags} from '../../hooks/useFeatureFlags';
import {FeatureFlags} from 'src/services/FeatureFlagService';
import {UniversityData} from './form/form';
import Text from 'shared/components/text';

interface Props {
  featureFlags: FeatureFlags;
}

function AdminUniversity({featureFlags}: Props) {
  const [universityData, setUniversityData] = useState<
    (AdminUniversityType & {useRanked: boolean}) | undefined
  >();
  const universityId = useQuery().get('id');
  const slug = useQuery().get('slug');
  const {path} = useRouteMatch();
  const [tabs, setTabs] = useState<RoutedTabsProps['tabs']>([
    {label: 'Edit', path: '', queryString: `id=${universityId}`},
  ]);

  const fetchUniversity = async () => {
    try {
      let response;
      if (universityId) {
        response = await AdminUniversityService.show(universityId);
      } else if (slug) {
        response = await AdminUniversityService.new(slug);
      }

      const photoListings = await req<{useRanked: boolean}>({
        url: `/admin/university/${universityId}/college_images`,
      });

      setUniversityData({...response, useRanked: photoListings.useRanked});
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (!isEmpty(universityId) || !isEmpty(slug)) {
      fetchUniversity();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [universityId, slug]);

  useEffect(() => {
    const newTabs: RoutedTabsProps['tabs'] = [
      {label: 'Edit', path: '', queryString: `id=${universityId}`},
    ];

    if (featureFlags.endpointConfigurations) {
      newTabs.push({
        label: 'Endpoint Configurations',
        path: '/endpoint-configurations',
        queryString: `id=${universityId}`,
      });
    }

    if (featureFlags.reportIntegrations) {
      newTabs.push({
        label: 'Report Integrations',
        path: '/report-integrations',
        queryString: `id=${universityId}`,
      });
    }

    setTabs(newTabs);
  }, [featureFlags, universityId]);

  const transformedUniversity = useMemo(() => {
    if (!universityData) return null;

    let {listing, university} = universityData;
    university = university || {};

    return {
      ...university,
      joinPageSlug: universityData.join_page_slug,
      slug: listing.slug,
      useRanked: universityData.useRanked,
      collegeListingId: listing._id,
    } as unknown as UniversityData;
  }, [universityData]);

  if (isEmpty(universityData)) {
    return <Loading />;
  }

  return (
    <>
      <Text header weight={1}>
        {universityData?.listing?.name}
      </Text>
      {universityId && tabs.length > 1 && <RoutedTabs tabs={tabs} />}
      <Switch>
        {universityId && featureFlags.endpointConfigurations && (
          <Route path={`${path}/endpoint-configurations`}>
            <EndpointConfigurationsTab universityId={universityId} />
          </Route>
        )}
        {universityId && universityData && featureFlags.reportIntegrations && (
          <Route path={`${path}/report-integrations`}>
            <ReportIntegrations
              universityId={universityId}
              enableReportIntegrations={universityData.university.enableReportIntegrations}
            />
          </Route>
        )}
        <Route path={path}>
          {universityId && <RecentDownloads universityId={universityId} />}
          {universityData && transformedUniversity && (
            <UniversityForm name={universityData.listing.name} university={transformedUniversity} />
          )}
        </Route>
      </Switch>
    </>
  );
}

export default withFeatureFlags(
  AdminUniversity,
  ['endpointConfigurations', 'reportIntegrations'],
  false
);
